(ns ui.components.stepper.view
  (:require
   [reagent.core :as r]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.util :as uu]))

(defn- ellipsis-visible-indices
  "Returns sorted indices to show when using :with-ellipsis variant.
   Always shows first, last, active, and active's neighbors.
   If total <= 5, shows all."
  [{:keys [total active]}]
  (cond
    (<= total 0) []
    (<= total 5) (vec (range total))
    :else
    (let [last-idx (dec total)]
      (->> #{0 last-idx active
             (max 0 (dec active))
             (min last-idx (inc active))}
           sort
           vec))))

(defn- chevron-icon []
  [:svg {:xmlns   "http://www.w3.org/2000/svg"
         :viewBox "0 0 24 24"
         :fill    "none"
         :stroke  "currentColor"
         :class   "tw2-h-3 tw2-w-3"
         :stroke-width "2.5"
         :stroke-linecap "round"
         :stroke-linejoin "round"}
   [:polyline {:points "9 18 15 12 9 6"}]])

(defn- ellipsis-icon []
  [:svg {:xmlns   "http://www.w3.org/2000/svg"
         :viewBox "0 0 24 24"
         :fill    "currentColor"
         :class   "tw2-h-3 tw2-w-3"}
   [:circle {:cx "12" :cy "12" :r "1.5"}]
   [:circle {:cx "5"  :cy "12" :r "1.5"}]
   [:circle {:cx "19" :cy "12" :r "1.5"}]])

(defn- separator [{:keys [state kind hidden-steps]}]
  (let [color-class (if (= state :past)
                      "tw2-text-link-secondary"
                      "tw2-text-link-disabled")]
    (if (= kind :ellipsis)
      [tooltip/view
       {:side :bottom}
       [:button
        {:type       "button"
         :class      (uu/cm "tw2-shrink-0 tw2-border-0 tw2-bg-transparent tw2-p-0 tw2-cursor-default" color-class)
         :aria-label "Show hidden steps"}
        [ellipsis-icon]]
       [:ul {:class "tw2-list-disc tw2-pl-4 tw2-space-y-1 tw2-text-sm"}
        (for [[i label] (map-indexed vector hidden-steps)]
          ^{:key i}
          [:li label])]]
      [:span
       {:class     (uu/cm "tw2-shrink-0" color-class)
        :aria-hidden "true"}
       [chevron-icon]])))

(defn- step-link [{:keys [label href active? future? on-step-click index]}]
  [:li {:class "tw2-flex tw2-items-center tw2-shrink-0"}
   [:a
    (cond-> {:href  (or href "#")
             :class (uu/cm "tw2-font-semibold tw2-text-sm tw2-leading-5 tw2-no-underline tw2-whitespace-nowrap"
                           (cond
                             future? "tw2-text-link-disabled tw2-pointer-events-none"
                             active? "tw2-text-link-secondary"
                             :else   "tw2-text-link-secondary hover:tw2-text-link-secondary-hover tw2-cursor-pointer"))}
      active?                (assoc :aria-current "step")
      future?                (assoc :aria-disabled "true")
      (and on-step-click
           (not future?))    (assoc :on-click (fn [e]
                                                (.preventDefault e)
                                                (on-step-click index))))
    label]])

(defn- step-list [{:keys [steps active-step on-step-click visible-indices scroll-variant? active-step-ref]}]
  [:ol {:class "tw2-flex tw2-items-center tw2-gap-2 tw2-list-none tw2-m-0 tw2-p-0 tw2-whitespace-nowrap"}
   (doall
    (for [[visible-pos index] (map-indexed vector visible-indices)
          :let [step        (nth steps index)
                next-index  (get visible-indices (inc visible-pos))
                active?     (= index active-step)
                future?     (> index active-step)
                sep-kind    (when next-index
                              (if (= next-index (inc index))
                                :chevron
                                :ellipsis))
                sep-state   (if (<= index active-step) :past :future)
                hidden      (when (= sep-kind :ellipsis)
                              (->> (subvec steps (inc index) next-index)
                                   (map :label)
                                   (remove nil?)))]]
      ^{:key (str (:label step) "-" index)}
      [:<>
       [:div {:ref (when (and scroll-variant? active?)
                     active-step-ref)}
        [step-link {:label         (:label step)
                    :href          (:href step)
                    :active?       active?
                    :future?       future?
                    :on-step-click on-step-click
                    :index         index}]]
       (when sep-kind
         [separator {:state        sep-state
                     :kind         sep-kind
                     :hidden-steps hidden}])]))])

(defn- scroll-to-active! [container-el active-el]
  (when (and container-el active-el)
    (let [container-rect (.getBoundingClientRect container-el)
          active-rect    (.getBoundingClientRect active-el)
          active-center  (+ (- (.-left active-rect) (.-left container-rect))
                            (/ (.-width active-rect) 2))
          target         (+ (.-scrollLeft container-el)
                            (- active-center (/ (.-clientWidth container-el) 2)))
          max-scroll     (max 0 (- (.-scrollWidth container-el) (.-clientWidth container-el)))
          clamped        (min (max 0 target) max-scroll)]
      (.scrollTo container-el #js {:left clamped}))))

(defn- mobile-progress-bar [{:keys [value max-val]}]
  (let [pct (if (and max-val (pos? max-val))
              (min 100 (* 100 (/ value max-val)))
              0)]
    [:div {:class "tw2-w-full tw2-h-1 tw2-bg-gray-200 tw2-rounded-full tw2-overflow-hidden"}
     [:div {:class "tw2-h-full tw2-bg-progress-bar-filled-bg tw2-rounded-full tw2-transition-all"
            :style {:width (str pct "%")}}]]))

(defn view
  "Stepper component matching zap-kit API.

   Props:
   - :steps        - vector of {:label \"...\" :href \"...\"}
   - :active-step  - zero-based index of current step
   - :on-step-click - (fn [index]) called when a past/active step is clicked
   - :variant      - :all-visible (default), :with-scroll, :with-ellipsis, :mobile
   - :class        - additional CSS classes"
  [_]
  (let [container-ref (r/atom nil)
        active-ref    (r/atom nil)]
    (r/create-class
     {:component-did-update
      (fn [this _]
        (let [{:keys [active-step variant]} (r/props this)]
          (when (and (= variant :with-scroll)
                     (>= active-step 2))
            (scroll-to-active! @container-ref @active-ref))))

      :component-did-mount
      (fn [this]
        (let [{:keys [active-step variant]} (r/props this)]
          (when (and (= variant :with-scroll)
                     (>= active-step 2))
            (scroll-to-active! @container-ref @active-ref))))

      :reagent-render
      (fn [{:keys [steps active-step on-step-click variant class]}]
        (let [variant     (or variant :all-visible)
              scroll?     (= variant :with-scroll)
              ellipsis?   (= variant :with-ellipsis)
              mobile?     (= variant :mobile)
              total       (count steps)
              clamped     (min (max (or active-step 0) 0) (max (dec total) 0))
              vis-indices (if ellipsis?
                            (ellipsis-visible-indices {:total total :active clamped})
                            (vec (range total)))]
          (if mobile?
            (let [step-label (:label (nth steps clamped nil))
                  pct        (if (pos? total)
                               (Math/round (* 100 (/ (inc clamped) total)))
                               0)]
              [:div {:class (uu/cm "tw2 tw2-flex tw2-flex-col tw2-items-start tw2-w-full tw2-max-w-xs" class)}
               [:p {:class "tw2-font-semibold tw2-text-sm tw2-leading-5 tw2-text-link-secondary tw2-whitespace-nowrap tw2-m-0"}
                step-label]
               [:div {:class "tw2-mt-2 tw2-w-full"}
                [mobile-progress-bar {:value (inc clamped) :max-val total}]]
               [:div {:class "tw2-mt-1 tw2-flex tw2-w-full tw2-items-start tw2-justify-between tw2-text-xs tw2-text-link-disabled tw2-m-0"}
                [:p {:class "tw2-m-0"} (str "Step " (inc clamped) " out of " total)]
                [:p {:class "tw2-m-0 tw2-text-right"} (str pct "%")]]])
            [:nav
             {:class      (uu/cm "tw2 tw2-flex tw2-items-center tw2-font-sans tw2-text-sm tw2-font-semibold tw2-leading-5 tw2-max-w-full tw2-overflow-hidden"
                                 (when scroll? "tw2-w-full")
                                 class)
              :aria-label "Stepper"}
             (if scroll?
               [:div {:ref   #(reset! container-ref %)
                      :class "tw2-overflow-x-auto tw2-pb-1 tw2-w-full"}
                [:div {:class "tw2-min-w-max"}
                 [step-list {:steps            steps
                             :active-step      clamped
                             :on-step-click    on-step-click
                             :visible-indices  vis-indices
                             :scroll-variant?  true
                             :active-step-ref  #(reset! active-ref %)}]]]
               [step-list {:steps           steps
                           :active-step     clamped
                           :on-step-click   on-step-click
                           :visible-indices vis-indices}])])))
      :display-name "stepper"})))
